/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import cx from 'classnames';
import { FormattedMessage } from 'react-intl';

import AppUtils from '../../../../utilities/AppUtils';

import { BodyMain, Small } from '../../../components/Text';
import { TextLink } from '../../../components/TextLink';

import { ShareCardColorModel } from '../../../appAuthorized/dataTransformComponents/ColorShareCard';
import CategorySelect from './CategorySelect';
import Colors from '../../../styles/Colors';
import UrlAccessCodeChange from './UrlAccessCodeChange';
import ShareCardSection from '../components/ShareCardSection';
import { ProfileThemeedWarningModal } from '../../../components/modal/WarningModal';
import ShareCardModel from '../../../../models/ShareCardModel';
import FlexRow from '../../../components/FlexRow';
import { useUIState } from '../../../hooks/useUIState';

interface ContentAreaProps {
  shareCard: any;
  shareCardColorModel: ShareCardColorModel;
  excluded: boolean;
  included: boolean;
  onChangePasscode(): void;
}
const ContentArea: React.FC<ContentAreaProps> = (props) => {
  const { shareCard, shareCardColorModel, excluded, included, onChangePasscode } = props;
  const shareCardColor = shareCardColorModel.cssColor(1);

  if (excluded) {
    return (
      <div>
        <BodyMain className={excluded ? 'font-color-gsMiddle' : ''}>
          <FormattedMessage id="shareCard.urlExcluded.message.text" />
        </BodyMain>
        <div style={{ height: 19 }} />
      </div>
    );
  } else if (included) {
    return (
      <div>
        <BodyMain>
          <FormattedMessage id="shareCard.url.display.text" values={{ string1: AppUtils.getShareUrlSubdomainPrefix(), string2: shareCard.data.uid }} />
        </BodyMain>

        <div>
          <Small>
            <FormattedMessage id="shareCard.accessCode.display.text" values={{ string1: shareCard.data.access_code }} />
          </Small>
          <TextLink
            className="font-caption-1"
            style={{ color: shareCardColor, paddingLeft: 5 }}
            onClick={onChangePasscode}
          >
            <FormattedMessage id="shareCardEdit.accessCodeChange.button.label" />
          </TextLink>
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <BodyMain className={excluded ? 'font-color-gsMiddle' : ''}>
          <FormattedMessage id="shareCard.urlDisabled.message.text" />
        </BodyMain>
        <div style={{ height: 19 }} />
      </div>
    );
  }
};

interface Props  {
  shareCard: any;
  languageCode: string;
  shareCardColorModel: ShareCardColorModel;
  updateShareCard(shareCard, callback): void;
  goBackToEdit(): void;
}

const ShareCardEditPublicUrl: React.FC<Props> = (props) => {
  const { shareCard, shareCardColorModel, updateShareCard, goBackToEdit } = props;

  const [, uiActions] = useUIState();

  const excluded = shareCard.shareCardURL.isExcluded();
  const required = shareCard.shareCardURL.isRequired();
  const included = shareCard.shareCardURL.isIncluded();

  const toggleUrl = () => {
    const isEnabled = ShareCardModel.isURLAccessible(shareCard.data);
    const isEmergencyCard = ShareCardModel.isEmergencyShareCard(shareCard.definition);
    const phrases = isEmergencyCard ? {
      title: { id: 'shareCardEdit.emergency.disableURL.heading' },
      body: { id: 'shareCardEdit.emergency.disableURL.message.md' },
      button: { id: 'shareCardEdit.disableURL.button.label' },
    } : {
      title: { id: 'shareCardEdit.disableURL.heading' },
      body: { id: 'shareCardEdit.disableURL.message.md' },
      button: { id: 'shareCardEdit.disableURL.button.label' },
    };

    if (isEnabled) {
      uiActions.pushDarkBigBlur(
        <ProfileThemeedWarningModal
          phraseMessages={phrases}
          onDismiss={() => {}}
          onConfirm={() => {
            const newData = ShareCardModel.toggleURL(shareCard.data, false);
            updateShareCard(newData, () => {});
          }}
          hasCloseButton={true}
          isBodyPreformatted={true}
        />,
      );
    } else {
      const newData = ShareCardModel.toggleURL(shareCard.data, true);
      updateShareCard(newData, () => {});
    }
  };

  return (
    <ShareCardSection shareCardColorModel={shareCardColorModel}>
      <FlexRow>
        <div
          className={cx('fill-color-transparent', {
            'font-color-input-inactive': excluded,
          })}
        >
          <div className="font-subhead">
            <FormattedMessage id="shareCard.url.field.label" />
          </div>

          <ContentArea
            shareCard={shareCard}
            shareCardColorModel={shareCardColorModel}
            excluded={excluded}
            included={included}
            onChangePasscode={() => {
              uiActions.pushDarkBigBlur(
                <UrlAccessCodeChange
                  goBackToEdit={goBackToEdit}
                  onSubmit={(passcode, callback) => {
                    const s = shareCard;
                    s.passcode = passcode;
                    updateShareCard(s.data, callback);
                  }}
                  shareCard={shareCard}
                />,
              );
            }}
          />
        </div>

        {
          excluded ? null : (
            <div onClick={required ? null : toggleUrl}>
              <CategorySelect
                selectedTotal={included ? 1 : 0}
                allTotal={1}
                color={required ? Colors.middleGray : shareCardColorModel.cssColor()}
                showTotals={false}
                zeroSelected={included}
              />
            </div>
          )
        }
      </FlexRow>
    </ShareCardSection>
  );
};

export default ShareCardEditPublicUrl;
